import { useCreateMutation, useCurrentWorkspace } from '@gripp/shared-logic';
import {
  Button,
  TextInput as TextInputCropCircle,
  tokens,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import * as yup from 'yup';

const CREATE_GROUP = `
  mutation createGroup($input: CreateGroupInput!) {
    createGroup(input: $input) {
      id
    }
  }
`;

const formSchema = yup.object({
  name: yup.string().required('Group name is required'),
});

type Props = {
  onComplete: () => void;
};

export const GroupNewPanel = ({ onComplete }: Props) => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();
  const { mutateAsync, isPending } = useCreateMutation({
    modelName: 'group',
    query: CREATE_GROUP,
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  const onSubmit = async (data: { name: string }) => {
    if (!currentWorkspace) return;
    await mutateAsync({
      input: { name: data.name, workspace: { id: currentWorkspace.id } },
    });
    onComplete();
  };

  const canSave = isDirty && isValid && !isPending;

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Controller
          control={control}
          name="name"
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInputCropCircle
              label={t('groups.editGroup.nameLabel', { defaultValue: 'Name' })}
              placeholder={t('groups.create.placeholder')}
              value={value}
              onBlur={onBlur}
              width="100%"
              hideBorder
              onChangeText={onChange}
            />
          )}
        />
      </View>
      <View style={styles.footer}>
        <Button
          variant="rectangular"
          size="large"
          state={canSave ? 'active' : 'default'}
          disabled={!canSave}
          onPress={handleSubmit(onSubmit)}
          style={styles.saveButton}
          testID="group-new-save-button"
        >
          {t('groups.create.save')}
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'space-between',
  },
  content: {
    padding: tokens.spacing.lg,
  },
  footer: {
    padding: tokens.spacing.lg,
  },
  saveButton: {
    width: '100%',
  },
});
